import { Modal, Row, Col, Button, FormGroup, Input } from 'reactstrap';
import { useContext, useState, useEffect } from 'react';
import { Context as AuthContext } from '../../../contexts/AuthContext';
import ClassReportService from '../../../services/ClassReport';
import { toast } from 'react-toastify';
import moment from 'moment';
import InputMask from 'react-input-mask';
import FileDownload from 'js-file-download';
import * as XLSX from 'xlsx-js-style';
import * as FileSaver from 'file-saver';

type ClassReportModalProps = {
  isOpen: boolean;
  classId: string;
  toggleModal: (e?: any) => void;
};

const ClassReportModal = ({ isOpen, classId, toggleModal }: ClassReportModalProps) => {
  const { token }: any = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [weekInterval, setWeekInterval] = useState('');
  const [weekList, setWeekList] = useState([] as { value: string; label: string }[]);
  const [monthInterval, setMonthInterval] = useState('');
  const [checkInInfoMonthInterval, setCheckInInfoMonthInterval] = useState('');

  const textCenter = (text: string) => {
    return {
      v: text,
      t: 's',
      s: { alignment: { horizontal: 'center' } },
    };
  };

  useEffect(() => {
    if (isOpen) {
      let date = moment();
      setMonthInterval(date.format('MM/YYYY'));
      setCheckInInfoMonthInterval(date.format('MM/YYYY'));
      const weeks: { value: string; label: string }[] = [];
      for (let i = 1; i <= 12; i++) {
        weeks.push({
          value: `${date.startOf('isoWeek').format('YYYY-MM-DD')}|${date
            .endOf('isoWeek')
            .weekday(-2)
            .format('YYYY-MM-DD')}`,
          label: `${date.startOf('isoWeek').format('MM/DD/YYYY')} - ${date
            .endOf('isoWeek')
            .weekday(-2)
            .format('MM/DD/YYYY')}`,
        });
        date = date.subtract(1, 'weeks');
      }
      setWeekInterval(weeks[0].value);
      setWeekList(weeks);
    }
  }, [isOpen]);

  const isValidMonth = (value: string) => /^(0[1-9]|1[0-2])\/\d{4}$/.test(value || '');

  const handleSubmit = async (e: React.FormEvent, reportType: string) => {
    e.preventDefault();
    if (loading) return;

    setLoading(true);

    try {
      if (reportType === 'MONTHLY') {
        if (!isValidMonth(monthInterval)) return toast.warn('Please enter a valid month (MM/YYYY).');
        const interval = monthInterval.split('/');
        const response = await ClassReportService(token).monthlyAttendance(classId, interval[1], interval[0]);
        FileDownload(response, 'monthly-report.pdf');
      }
      if (reportType === 'WEEKLY') {
        if (!weekInterval) return toast.warn('Please select the week.');
        const interval = weekInterval.split('|');
        const response = await ClassReportService(token).weeklyAttendance(classId, interval[0], interval[1]);
        FileDownload(response, 'weekly-report.pdf');
      }
      if (reportType === 'CHECKIN-INFO') {
        if (!isValidMonth(checkInInfoMonthInterval)) return toast.warn('Please enter a valid month (MM/YYYY).');
        const [month, year] = checkInInfoMonthInterval.split('/');
        const response = await ClassReportService(token).attendanceByCheckinInfo(
          classId,
          [year, month, '01'].join('-'),
          moment([year, month, '01'].join('-')).endOf('month').format('YYYY-MM-DD'),
        );
        const fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
        const wb = XLSX.utils.book_new();

        let day = JSON.parse(JSON.stringify(response.data.meta.startDate));

        const lineMonths: any[] = [''];
        const lineDays: any[] = [''];
        const lineHeaders: any[] = ['Name'];
        let merges: any[] = [];

        let col: number = 1;
        while (moment(day).isSameOrBefore(response.data.meta.endDate)) {
          if ([0, 6].includes(moment(day).day())) {
            day = moment(day).add(1, 'days').format('YYYY-MM-DD');
            continue;
          }
          lineMonths.push(moment(day).format('MMMM'));
          lineMonths.push('');
          lineMonths.push('');
          lineMonths.push('');
          lineMonths.push('');
          lineMonths.push('');
          lineMonths.push('');
          lineMonths.push('');
          lineMonths.push('');
          lineMonths.push('');
          lineMonths.push('');
          lineMonths.push('');
          lineDays.push(textCenter(moment(day).format('ddd. MMM D, YYYY')));
          lineDays.push('');
          lineDays.push('');
          lineDays.push('');
          lineDays.push('');
          lineDays.push('');
          lineDays.push('');
          lineDays.push('');
          lineDays.push('');
          lineDays.push('');
          lineDays.push('');
          lineDays.push('');
          lineHeaders.push('Check In');
          lineHeaders.push('Who');
          lineHeaders.push('Check Out');
          lineHeaders.push('Who');
          lineHeaders.push('Check In');
          lineHeaders.push('Who');
          lineHeaders.push('Check Out');
          lineHeaders.push('Who');
          lineHeaders.push('Check In');
          lineHeaders.push('Who');
          lineHeaders.push('Check Out');
          lineHeaders.push('Who');
          day = moment(day).add(1, 'days').format('YYYY-MM-DD');
          merges.push({ s: { c: col, r: 1 }, e: { c: col + 11, r: 1 } });
          col += 12;
        }
        merges = [{ s: { c: 1, r: 0 }, e: { c: lineMonths.length - 1, r: 0 } }, ...merges];

        const xlsData: any[] = [
          lineMonths,
          lineDays,
          lineHeaders,
          ...response.data.body.map((kid: any) => {
            const checks: any[] = [];
            Object.keys(kid.checks).forEach((data: any) => {
              checks.push(textCenter(kid.checks[data].in[0]?.time || '-'));
              checks.push(textCenter(kid.checks[data].in[0]?.user || '-'));
              checks.push(textCenter(kid.checks[data].out[0]?.time || '-'));
              checks.push(textCenter(kid.checks[data].out[0]?.user || '-'));

              checks.push(textCenter(kid.checks[data].in[1]?.time || '-'));
              checks.push(textCenter(kid.checks[data].in[1]?.user || '-'));
              checks.push(textCenter(kid.checks[data].out[1]?.time || '-'));
              checks.push(textCenter(kid.checks[data].out[1]?.user || '-'));

              checks.push(textCenter(kid.checks[data].in[2]?.time || '-'));
              checks.push(textCenter(kid.checks[data].in[2]?.user || '-'));
              checks.push(textCenter(kid.checks[data].out[2]?.time || '-'));
              checks.push(textCenter(kid.checks[data].out[2]?.user || '-'));
            });
            return [kid.name, ...checks];
          }),
        ];
        const ws = XLSX.utils.aoa_to_sheet(xlsData);
        ws['!merges'] = merges;
        if (!ws['!cols']) ws['!cols'] = [];
        if (!ws['!cols'][0]) ws['!cols'][0] = { wch: 25 };
        XLSX.utils.book_append_sheet(wb, ws, 'sheet 1');
        const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        const data = new Blob([excelBuffer], { type: fileType });
        FileSaver.saveAs(
          data,
          (
            'attendance-by-checkin-info_' +
            response.data.meta.class +
            '-' +
            moment(response.data.meta.startDate).format('MMMM') +
            '.xlsx'
          ).toLocaleLowerCase(),
        );
      }
    } catch (e: any) {
      const message = String(e?.response?.data?.message || 'Report error!');
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Modal className="modal-dialog-centered" isOpen={isOpen} toggle={(e: any) => toggleModal(e)}>
        <div className="modal-header pb-1">
          <h5 className="modal-title">Attendance Roster</h5>
          <button
            aria-label="Close"
            className="close"
            data-dismiss="modal"
            type="button"
            onClick={(e) => toggleModal(e)}
          >
            <span aria-hidden={true}>×</span>
          </button>
        </div>
        <div className="modal-body pt-0">
          <Row>
            <Col>
              <Row>
                <Col lg="12">
                  <FormGroup>
                    <label className="form-control-label">By week</label>
                    <Input type="select" onChange={(e) => setWeekInterval(e.target.value)} value={weekInterval}>
                      <option value=""></option>
                      {weekList?.map((w: any, index: number) => {
                        return (
                          <option value={w.value} key={`week-interval-${index}`}>
                            {w.label}
                          </option>
                        );
                      })}
                    </Input>
                    <Button
                      color="primary"
                      type="button"
                      size="sm"
                      className="mt-1"
                      style={{ minWidth: '100px' }}
                      onClick={(e) => handleSubmit(e, 'WEEKLY')}
                    >
                      OK
                    </Button>
                  </FormGroup>
                </Col>
                <Col lg="12">
                  <FormGroup>
                    <label className="form-control-label">By month</label>
                    <Input
                      mask="99/9999"
                      value={monthInterval}
                      tag={InputMask}
                      onChange={(e) => setMonthInterval(e.target.value)}
                    />
                    <Button
                      color="primary"
                      type="button"
                      size="sm"
                      className="mt-1"
                      style={{ minWidth: '100px' }}
                      onClick={(e) => handleSubmit(e, 'MONTHLY')}
                    >
                      OK
                    </Button>
                  </FormGroup>
                </Col>
                <Col lg="12">
                  <FormGroup>
                    <label className="form-control-label">By Check in info</label>
                    <Input
                      mask="99/9999"
                      value={checkInInfoMonthInterval}
                      tag={InputMask}
                      onChange={(e) => setCheckInInfoMonthInterval(e.target.value)}
                    />
                    <Button
                      color="primary"
                      type="button"
                      size="sm"
                      className="mt-1"
                      style={{ minWidth: '100px' }}
                      onClick={(e) => handleSubmit(e, 'CHECKIN-INFO')}
                    >
                      OK
                    </Button>
                  </FormGroup>
                </Col>
              </Row>
            </Col>
          </Row>
        </div>
      </Modal>
    </>
  );
};

export default ClassReportModal;
